"use strict";
/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Roles = exports.Customer = void 0;
const jwt_decode_1 = __importDefault(require("jwt-decode"));
const ApiError_1 = require("../cart/requestHandlers/ApiError");
const AddressResponseData_1 = require("../checkout/AddressResponseData");
const EventEmitterFactory_1 = require("../events/EventEmitterFactory");
const CustomerRequestHandler_1 = require("./requestHandlers/CustomerRequestHandler");
const VisitorData_1 = require("./VisitorData");
class Customer {
    constructor(visitorData, isBroadcastAllowed) {
        this.parsedToken = null;
        this.visitorData = visitorData;
        if (visitorData.token) {
            this.parsedToken = Customer.verifyToken(visitorData.token);
        }
        this.eventEmitter = EventEmitterFactory_1.EventEmitterFactory.getEmitter("CustomerStateChanged", isBroadcastAllowed);
        this.eventEmitter.on((payload) => {
            if (payload.visitorData === this.visitorData) {
                return;
            }
            this.visitorData = payload.visitorData;
            this.parsedToken = payload.token;
        });
        this.logIn = this.logIn.bind(this);
        this.logOut = this.logOut.bind(this);
        this.hasRole = this.hasRole.bind(this);
        this.getAvailablePoints = this.getAvailablePoints.bind(this);
        this.getLastAddresses = this.getLastAddresses.bind(this);
        this.setNewsletterOptions = this.setNewsletterOptions.bind(this);
        this.getNewsletterOptions = this.getNewsletterOptions.bind(this);
        this.getRatings = this.getRatings.bind(this);
        this.retrieveProperties = this.retrieveProperties.bind(this);
        this.putProperties = this.putProperties.bind(this);
        this.postAddress = this.postAddress.bind(this);
    }
    /**
     * Creates and verifies a new Customer from a visitor
     * @param {VisitorData} visitorData
     * @param {boolean} isBroadcastAllowed
     * @returns {Promise<Customer>}
     */
    static create(visitorData, isBroadcastAllowed = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Customer(visitorData, isBroadcastAllowed);
        });
    }
    /**
     * Verifies the token structure and its signature against the public key
     * @param {string} token
     * @returns {IJwtToken}
     */
    static verifyToken(token) {
        try {
            return jwt_decode_1.default(token);
        }
        catch (e) {
            throw new ApiError_1.ApiError("JWT decode failed: ", e);
        }
    }
    getAvailablePoints(shopId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.customerId === 0) {
                return [];
            }
            return yield CustomerRequestHandler_1.CustomerRequestHandler.getAvailablePoints(shopId, this.customerId);
        });
    }
    /**
     * Logs a customer in
     * @param {ICredentials} credentials
     * @returns {Promise<boolean>} Whether the login was successful or not
     */
    logIn(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawVisitorData = yield CustomerRequestHandler_1.CustomerRequestHandler.loginCustomer(credentials);
            if (!rawVisitorData) {
                return false;
            }
            const visitorData = new VisitorData_1.VisitorData(rawVisitorData);
            const token = Customer.verifyToken(visitorData.token);
            this.eventEmitter.emit({ token, visitorData });
            return true;
        });
    }
    /**
     * Logs the customer out
     * @returns {Promise<void>}
     */
    logOut() {
        return __awaiter(this, void 0, void 0, function* () {
            yield CustomerRequestHandler_1.CustomerRequestHandler.logoutCustomer();
            const token = yield CustomerRequestHandler_1.CustomerRequestHandler.getToken();
            const visitorData = new VisitorData_1.VisitorData(token);
            this.eventEmitter.emit({
                token: null,
                visitorData,
            });
        });
    }
    /**
     * requests customer's addresses used in the last checkout process
     * @param shopId
     */
    getLastAddresses(shopId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.customerId === 0) {
                return [];
            }
            const iAddressResponseData = yield CustomerRequestHandler_1.CustomerRequestHandler.getLastAddresses(shopId, this.customerId);
            const lastAddresses = [];
            iAddressResponseData.forEach((lastAddress) => {
                lastAddresses.push(new AddressResponseData_1.AddressResponseData(lastAddress));
            });
            return lastAddresses;
        });
    }
    /**
     * Sets customers address by api POST call. Returns the set address or undefined if customer is unknown
     * @param shopId sales channel id
     * @param address address properties
     */
    postAddress(shopId, address) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.customerId) {
                throw new Error("Tried a POST address without an existing customer id");
            }
            return yield CustomerRequestHandler_1.CustomerRequestHandler.postAddress(shopId, this.customerId, address);
        });
    }
    /**
     * request magic link via mail
     * @param email recipients email address
     * @param redirectUrl url placed in the email to redirect the user to
     */
    requestMagicLink(email, redirectUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            yield CustomerRequestHandler_1.CustomerRequestHandler.requestMagicLink(email, redirectUrl);
        });
    }
    /**
     * request new password via mail
     * @param email
     */
    requestPassword(email) {
        return __awaiter(this, void 0, void 0, function* () {
            yield CustomerRequestHandler_1.CustomerRequestHandler.requestPassword(email);
        });
    }
    /**
     * Puts newsletter options to user
     * @param {number} shopId
     * @param data
     * @returns {Promise<INewsletter[]>}
     */
    setNewsletterOptions(shopId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CustomerRequestHandler_1.CustomerRequestHandler.setNewsletterOptions(shopId, this.customerId, data);
        });
    }
    /**
     * Gets newsletter options to user
     * @param {number} shopId
     * @returns {Promise<INewsletter[]>}
     */
    getNewsletterOptions(shopId) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CustomerRequestHandler_1.CustomerRequestHandler.getNewsletterOptions(shopId, this.customerId);
        });
    }
    /**
     * Gets ratings to user
     * @param {number} shopId
     * @param {number | null} limit
     * @param {number | null} offset
     * @param {boolean | null} isRated
     * @returns {Promise<ICustomerRatingsResult[]>}
     */
    getRatings(shopId, limit, offset, isRated) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CustomerRequestHandler_1.CustomerRequestHandler.getRatings(shopId, this.customerId, limit, offset, isRated);
        });
    }
    getReceipts(shopId, type) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CustomerRequestHandler_1.CustomerRequestHandler.getReceipts(shopId, this.customerId, type);
        });
    }
    /**
     * Retrieves customer's properties or returns undefined if customer is unknown
     * @param shopId sales channel id
     */
    retrieveProperties(shopId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.customerId) {
                throw new Error("Tried a POST address without an existing customer id");
            }
            return yield CustomerRequestHandler_1.CustomerRequestHandler.retrieveProperties(shopId, this.customerId);
        });
    }
    ;
    /**
     * Sets customer's properties by customerId and token, or returns undefined if customer is
     * unknown. If customer doesn't exist or is not recognised (no token/customerId == 0) method
     * `CustomerFactory.createOrUpdateCustomer(<customersEmailAddress>)` has to be called.
     * @param shopId Sales channel id
     * @param properties
     */
    putProperties(shopId, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.customerId) {
                throw new Error("Tried a PUT properties without an existing customer id");
            }
            return yield CustomerRequestHandler_1.CustomerRequestHandler.updateProperties(shopId, this.customerId, properties);
        });
    }
    newsletterOptin(shopId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield CustomerRequestHandler_1.CustomerRequestHandler.newsletterOptin(shopId, this.customerId);
        });
    }
    newsletterOptout(shopId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield CustomerRequestHandler_1.CustomerRequestHandler.newsletterOptout(shopId, this.customerId);
        });
    }
    /**
     * Whether the customer is recognized (at least)
     * @returns {boolean}
     */
    get isRecognised() {
        return this.hasRole(Roles.SHOP);
    }
    /**
     * An ID identifying the visitor
     * @returns {string}
     */
    get visitorId() {
        return this.visitorData.visitorId;
    }
    /**
     * The raw jw-token, if present
     * @returns {string | null}
     */
    get token() {
        return this.visitorData.token;
    }
    /**
     * Whether the customer is logged in
     * @returns {boolean}
     */
    get isLoggedIn() {
        return this.hasRole(Roles.SHOP_ELEVATED);
    }
    /**
     * return if customer is registered for newsletter
     * @returns {boolean}
     */
    get newsletterFlag() {
        var _a;
        return !!((_a = this.parsedToken) === null || _a === void 0 ? void 0 : _a.newsletter_flag);
    }
    /**
     * Whether the customer has a specific role
     * @param {Roles} role
     * @returns {boolean}
     */
    hasRole(role) {
        if (!this.parsedToken) {
            return false;
        }
        return this.parsedToken.realm_access.roles.indexOf(role) > -1;
    }
    /**
     * The customers email address
     * @returns {string | null}
     */
    get email() {
        return this.parsedToken ? this.parsedToken.email : null;
    }
    /**
     * The isCorporateCustomer flag
     * @returns {boolean}
     */
    get isCorporateCustomer() {
        return this.parsedToken ? this.parsedToken.isCorporateCustomer : false;
    }
    /**
     * The customers id
     * @returns {number}
     */
    get customerId() {
        return this.parsedToken ? this.parsedToken.customer_id : 0;
    }
    /**
     * The group of customers this specific customer belongs to
     * @returns {number}
     */
    get customerGroupId() {
        return this.parsedToken ? this.parsedToken.customer_group_id : 0;
    }
    /**
     * The emitter to listen on for CustomerChange events
     * @returns {IEventEmitter<ICustomerDataset>}
     */
    get emitter() {
        return this.eventEmitter;
    }
    /**
     * Information about the tabs involved in broadcast emitting
     * @returns {IBusLogging}
     */
    get tabInfo() {
        return this.emitter.tabInfo;
    }
}
exports.Customer = Customer;
var Roles;
(function (Roles) {
    Roles["SHOP"] = "SHOP";
    Roles["SHOP_ELEVATED"] = "SHOP_ELEVATED";
})(Roles = exports.Roles || (exports.Roles = {}));
